/**
 * SettingsContext — Global JAPAP public settings shared by every module.
 *
 * iter284 Phase B — Before this file existed, `/api/settings/public` was
 * fetched independently by 11+ components (AuthContext, WalletPage,
 * FeedPage, ChatPage, ServicesPage, MarketplaceAdsPage,
 * MarketplaceProductPage, GamesModule, ForecastPage…). Each caller
 * triggered its own network round-trip on mount, producing 3-4×
 * duplicate calls per page visit. This provider fetches ONCE at the
 * root of the app and exposes the settings via a stable hook.
 *
 * Sensitive data (KYC, balances, secrets, financial decisions) MUST
 * NEVER be pushed through this provider — the backend remains the
 * single source of truth for anything critical. Only the `settings/
 * public` payload (kill switches, feature flags, marketing toggles,
 * prefetch limits…) lives here.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const SettingsContext = createContext({
  settings: {},
  loading: true,
  refresh: () => {},
});

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const inflightRef = useRef(null);   // dedupe concurrent calls

  const refresh = useCallback(async () => {
    // Coalesce concurrent refresh() calls to a single network fetch.
    if (inflightRef.current) return inflightRef.current;
    const p = (async () => {
      try {
        const { data } = await axios.get(`${API}/api/settings/public`, {
          withCredentials: true, timeout: 4000,
        });
        setSettings(data || {});
      } catch { /* keep previous or empty — fail-open */ }
      finally {
        setLoading(false);
        inflightRef.current = null;
      }
    })();
    inflightRef.current = p;
    return p;
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({ settings, loading, refresh }),
                         [settings, loading, refresh]);
  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
